import { type MutableRefObject, useEffect } from 'react'

import {
  Divider,
  Form,
  FormControl,
  Header,
  Space,
  WithFormControlSelect,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import type moment from 'moment'
import * as Yup from 'yup'

import {
  RequestableCreditCardSelect,
  type RequestableCreditCardOption,
} from '@webapp/components/RequestableCreditCardSelect'
import {
  type Currency,
  type CurrencyName,
} from '@webapp/core/entities/external/Currency'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  computeExpenseLineItemTotalsUseCase,
  type TaxType,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import {
  ExpenseAccountCodeSelect,
  type ExpenseAccountSelectValue,
} from '@webapp/modules/expense/components/ExpenseAccountCodeSelect'
import {
  ExpenseFormReimbursementCustomizations,
  type ExpenseFormReimbursementCustomization,
} from '@webapp/modules/expense/entities/enums'
import {
  ExpenseTypes,
  type ExpenseTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { AirfareFields } from './components/AirfareFields'
import { DefaultFields } from './components/DefaultFields'
import { ExpenseTypeSelector } from './components/ExpenseTypeSelector/ExpenseTypeSelector'
import { LabourFields } from './components/LabourFields'
import { MealsFields } from './components/MealsFields'
import { MileageFields } from './components/MileageFields'
import { mapExpenseItemToFormSchema } from './mapExpenseItemToFormSchema'
import messages from './messages'

export type SelectFieldValue = { value: string; label: string }

export interface ExpenseLineItemFormSchema {
  reimbursable: boolean
  merchant: string
  transDate: moment.Moment | null
  subtotal: number
  taxAmount: number
  taxType: SelectFieldValue
  notes: string
  expenseType: ExpenseTypeEnum
  tips: number
  mileageRate: number
  distance: number
  unitOfDistanceStr: SelectFieldValue
  hourlyRate: number
  hoursOfWork: number
  airline: string
  ticketNumber: string
  departureDate: moment.Moment | null
  arrivalDate: moment.Moment | null
  account: ExpenseAccountSelectValue | null
  attachment: string
  paymentMethod: RequestableCreditCardOption | null
  currency: Currency | null
  totalCost: number
}

export interface ExpenseLineItemFormHandle {
  submitForm: () => Promise<void>
}

export interface IExpenseLineItemFormProps {
  item?: ExpenseItem
  baseCurrency?: Currency | null
  reimbursementSetting: ExpenseFormReimbursementCustomization
  secondaryCategoryId: number
  requesterId: number
  attachment: string
  isSubmitting: boolean
  onSubmit: (values: ExpenseLineItemFormSchema) => void
  formRef?: MutableRefObject<ExpenseLineItemFormHandle | null>
}

export const ExpenseLineItemForm = ({
  item,
  baseCurrency,
  reimbursementSetting,
  secondaryCategoryId,
  requesterId,
  attachment,
  isSubmitting,
  onSubmit,
  formRef,
}: IExpenseLineItemFormProps) => {
  const intl = useProcurifyIntl()

  const isReimbursementLocked =
    reimbursementSetting ===
      ExpenseFormReimbursementCustomizations.ALWAYS_YES ||
    reimbursementSetting === ExpenseFormReimbursementCustomizations.ALWAYS_NO

  const validationSchema = Yup.object({
    merchant: Yup.string()
      .required(intl.formatMessage(messages.vendorNameRequired))
      .trim(),
    transDate: Yup.mixed().required(intl.formatMessage(messages.dateRequired)),
    subtotal: Yup.number().required(),
    taxAmount: Yup.number().required(),
    notes: Yup.string().nullable(),
    account: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.accountRequired)),
    currency: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.currencyRequired)),
  })

  const validate = (values: ExpenseLineItemFormSchema) => {
    const errors: Record<string, string> = {}
    if (!values.reimbursable && values.paymentMethod == null) {
      errors.paymentMethod = intl.formatMessage(messages.paymentTypeRequired)
    }
    return errors
  }

  const initialValues = mapExpenseItemToFormSchema({
    item,
    baseCurrency,
    intl,
    reimbursementSetting,
    attachment,
  })

  const formik = useFormik<ExpenseLineItemFormSchema>({
    initialValues,
    validationSchema,
    validate,
    onSubmit,
    enableReinitialize: false,
  })

  useEffect(() => {
    if (formRef) {
      formRef.current = { submitForm: formik.submitForm }
    }
  }, [formRef, formik.submitForm])

  useEffect(() => {
    if (attachment !== formik.values.attachment) {
      formik.setFieldValue('attachment', attachment)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik.setFieldValue is not referentially stable in Formik 2.x
  }, [attachment])

  const unitOptions = [
    { label: intl.formatMessage(messages.unitKm), value: 'km' },
    { label: intl.formatMessage(messages.unitMi), value: 'mi' },
  ]

  const taxTypeOptions = [
    { label: intl.formatMessage(messages.taxTypePercent), value: '%' },
    { label: intl.formatMessage(messages.taxTypeDollar), value: '$' },
  ]

  const isNonReimbursable = !formik.values.reimbursable

  const isMeals = formik.values.expenseType === ExpenseTypes.MEALS
  const isMileage = formik.values.expenseType === ExpenseTypes.MILEAGE
  const isLabour = formik.values.expenseType === ExpenseTypes.LABOUR
  const isAirfare = formik.values.expenseType === ExpenseTypes.AIRFARE

  // Subtotal for Labour/Mileage is rate × quantity and is display-only.
  const { computedSubtotal } = computeExpenseLineItemTotalsUseCase.execute({
    expenseType: formik.values.expenseType,
    subtotal: formik.values.subtotal,
    taxAmount: formik.values.taxAmount,
    taxType: formik.values.taxType.value as TaxType,
    tips: formik.values.tips,
    mileageRate: formik.values.mileageRate,
    distance: formik.values.distance,
    hourlyRate: formik.values.hourlyRate,
    hoursOfWork: formik.values.hoursOfWork,
  })

  // Forward: any input feeding into total changes → recompute total.
  useEffect(() => {
    const { computedTotal } = computeExpenseLineItemTotalsUseCase.execute({
      expenseType: formik.values.expenseType,
      subtotal: formik.values.subtotal,
      taxAmount: formik.values.taxAmount,
      taxType: formik.values.taxType.value as TaxType,
      tips: formik.values.tips,
      mileageRate: formik.values.mileageRate,
      distance: formik.values.distance,
      hourlyRate: formik.values.hourlyRate,
      hoursOfWork: formik.values.hoursOfWork,
    })
    if (computedTotal !== formik.values.totalCost) {
      formik.setFieldValue('totalCost', computedTotal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik.setFieldValue is not referentially stable in Formik 2.x
  }, [
    formik.values.subtotal,
    formik.values.taxAmount,
    formik.values.taxType.value,
    formik.values.tips,
    formik.values.expenseType,
    formik.values.mileageRate,
    formik.values.distance,
    formik.values.hourlyRate,
    formik.values.hoursOfWork,
  ])

  // Reverse: user edits total → back-solve subtotal. The compute operations
  // are mutual inverses, so when the forward effect writes totalCost above,
  // this effect recomputes the same subtotal and the guard prevents a loop.
  useEffect(() => {
    if (isMileage || isLabour) return
    const { computedSubtotal: reverseSubtotal } =
      computeExpenseLineItemTotalsUseCase.executeReverse({
        expenseType: formik.values.expenseType,
        total: formik.values.totalCost,
        taxAmount: formik.values.taxAmount,
        taxType: formik.values.taxType.value as TaxType,
        tips: formik.values.tips,
      })
    if (reverseSubtotal !== formik.values.subtotal) {
      formik.setFieldValue('subtotal', reverseSubtotal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik.setFieldValue is not referentially stable in Formik 2.x
  }, [
    formik.values.totalCost,
    formik.values.expenseType,
    formik.values.taxAmount,
    formik.values.taxType.value,
    formik.values.tips,
    formik.values.subtotal,
    isMileage,
    isLabour,
  ])

  const currencyName = formik.values.currency?.name ?? ('' as CurrencyName)

  const handleCurrencyChange = (currency: Currency | null) => {
    formik.setFieldValue('currency', currency)
  }

  const handleAccountCodeChange = (
    selectedOption: ExpenseAccountSelectValue | null
  ) => {
    formik.setFieldValue('account', selectedOption)
  }

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <ExpenseTypeSelector
            value={formik.values.expenseType}
            onChange={(typeId) => formik.setFieldValue('expenseType', typeId)}
            disabled={isSubmitting}
          />

          <Divider />

          <Space direction='horizontal' justify='start' align='center'>
            <Form.Toggle
              id='reimbursable'
              name='reimbursable'
              label={intl.formatMessage(messages.needsReimbursementLabel)}
              checked={formik.values.reimbursable}
              disabled={isReimbursementLocked || isSubmitting}
            />

            <Space.Item grow={1}>
              {isNonReimbursable && (
                <WithFormControlSelect
                  id='paymentMethod'
                  name='paymentMethod'
                  label={intl.formatMessage(messages.paymentMethodLabel)}
                  required
                >
                  <RequestableCreditCardSelect
                    inputId='paymentMethod-select'
                    userId={requesterId}
                    value={formik.values.paymentMethod}
                    onChange={(option) =>
                      formik.setFieldValue('paymentMethod', option)
                    }
                    isDisabled={isSubmitting}
                    menuPortalTarget={document.body}
                  />
                </WithFormControlSelect>
              )}
            </Space.Item>
          </Space>

          <Space direction='horizontal'>
            <Space.Item grow={1}>
              <Form.TextField
                id='merchant'
                name='merchant'
                label={intl.formatMessage(messages.vendorNameLabel)}
                required
                placeholder={intl.formatMessage(messages.vendorNamePlaceholder)}
                disabled={isSubmitting}
              />
            </Space.Item>
            <Space.Item width={200}>
              <Form.SingleDatePicker
                id='transDate'
                name='transDate'
                label={intl.formatMessage(messages.dateLabel)}
                required
                disabled={isSubmitting}
              />
            </Space.Item>
          </Space>

          <FormControl
            name='account'
            id='accountCode'
            labelFor='accountCode-select'
            label={intl.formatMessage(messages.accountCodeLabel)}
            required
          >
            {({ error, handleBlur }) => (
              <ExpenseAccountCodeSelect
                inputId='accountCode-select'
                aria-labelledby='accountCode-label'
                secondaryCategoryId={secondaryCategoryId}
                requesterId={requesterId}
                value={formik.values.account}
                onChange={(account) => {
                  handleAccountCodeChange(account)
                  handleBlur()
                }}
                isDisabled={isSubmitting}
                menuPortalTarget={document.body}
                error={error}
              />
            )}
          </FormControl>

          <Form.TextField
            id='notes'
            name='notes'
            label={intl.formatMessage(messages.notesLabel)}
            placeholder={intl.formatMessage(messages.notesPlaceholder)}
            disabled={isSubmitting}
          />

          <Divider />

          <Header as='h5' gutter>
            {intl.formatMessage(messages.financialDetailsLabel)}
          </Header>

          {isMeals && (
            <MealsFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              taxTypeOptions={taxTypeOptions}
              isSubmitting={isSubmitting}
            />
          )}

          {isMileage && (
            <MileageFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              taxTypeOptions={taxTypeOptions}
              unitOptions={unitOptions}
              computedSubtotal={computedSubtotal}
              computedTotal={formik.values.totalCost}
              isSubmitting={isSubmitting}
            />
          )}

          {isLabour && (
            <LabourFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              taxTypeOptions={taxTypeOptions}
              computedSubtotal={computedSubtotal}
              computedTotal={formik.values.totalCost}
              isSubmitting={isSubmitting}
            />
          )}

          {isAirfare && (
            <AirfareFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              taxTypeOptions={taxTypeOptions}
              isSubmitting={isSubmitting}
            />
          )}

          {!isMeals && !isMileage && !isLabour && !isAirfare && (
            <DefaultFields
              currencyName={currencyName}
              selectedCurrency={formik.values.currency}
              onCurrencyChange={handleCurrencyChange}
              taxTypeOptions={taxTypeOptions}
              isSubmitting={isSubmitting}
            />
          )}
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}
